/**
 * PAGES REFRESH WHEN YOU COME BACK TO THE TAB (Jeremy, 1 Nov 2026, punch list
 * F172). Every screen loads its data once, when it opens; somebody who leaves
 * the scheduler in a tab and comes back an hour later was looking at the
 * hour-old version. Now, when the tab becomes visible again (or the window
 * takes focus), the screen on show reads its data again -- QUIETLY: the old
 * answer stays on screen until the new one replaces it, so there is no
 * "Loading…" flash and no jump in the scroll.
 *
 * NEVER while somebody is editing: a pop-up with a field in it (Report a
 * problem), the cursor in a field, or a field typed into that is still on the
 * page and not empty -- a half-made request, a deny reason. Search boxes,
 * checkboxes and drop-downs used as filters do not count. The rules and their
 * tests are `@scheduler/shared` (refresh.ts); this is the browser side.
 *
 * At most once every 15 seconds per screen, never in the first 15 seconds
 * after it opened; `visibilitychange` and `focus` arrive together on a return
 * and make one check.
 */
import { dueForRefresh, isEditing, isTypedField, type FieldLike } from "@scheduler/shared";
import { useEffect, useState } from "react";

/** How long after the first return signal the check runs, so `focus` and `visibilitychange` make one. */
const SETTLE_MS = 250;

/** Fields typed into since the page loaded; one that has left the page is dropped at the next check. */
let touched: Set<Element> | null = null;

function trackTyping(): void {
  if (touched !== null) return;
  const set = new Set<Element>();
  touched = set;
  document.addEventListener(
    "input",
    (e) => {
      const el = e.target;
      if (el instanceof Element && isTypedField(el as unknown as FieldLike)) set.add(el);
    },
    true,
  );
  // A submitted form has saved what was typed in it.
  document.addEventListener(
    "submit",
    (e) => {
      const form = e.target;
      if (!(form instanceof Element)) return;
      for (const el of set) if (form.contains(el)) set.delete(el);
    },
    true,
  );
}

/** The editing check, read from the live document. */
function editingInDocument(): boolean {
  const dialogFields = Array.from(
    document.querySelectorAll(
      '[role="dialog"] input, [role="dialog"] textarea, [role="dialog"] select, [role="dialog"] [contenteditable="true"], dialog[open] input, dialog[open] textarea, dialog[open] select',
    ),
  ) as unknown as FieldLike[];
  const live: FieldLike[] = [];
  for (const el of touched ?? []) {
    if (el.isConnected) live.push(el as unknown as FieldLike);
    else touched?.delete(el);
  }
  return isEditing({ dialogFields, active: document.activeElement as unknown as FieldLike | null, touched: live });
}

/**
 * A number that goes up each time the screen should quietly read its data again. Put it in the
 * dependencies of the effect that loads the screen. `whileEditing` is for a count in the header
 * (Approvals'), which changes nothing anybody is typing into.
 */
export function useRefreshOnReturn(options?: { whileEditing?: boolean }): number {
  const whileEditing = options?.whileEditing === true;
  const [tick, setTick] = useState(0);

  useEffect(() => {
    trackTyping();
    let last = Date.now();
    let timer: number | undefined;

    const check = (): void => {
      timer = undefined;
      if (document.visibilityState !== "visible") return;
      const now = Date.now();
      if (!dueForRefresh(now, last)) return;
      if (!whileEditing && editingInDocument()) return;
      last = now;
      setTick((n) => n + 1);
    };
    const onReturn = (): void => {
      if (timer === undefined) timer = window.setTimeout(check, SETTLE_MS);
    };

    document.addEventListener("visibilitychange", onReturn);
    window.addEventListener("focus", onReturn);
    return () => {
      document.removeEventListener("visibilitychange", onReturn);
      window.removeEventListener("focus", onReturn);
      if (timer !== undefined) window.clearTimeout(timer);
    };
  }, [whileEditing]);

  return tick;
}
